import { useState, useEffect, useRef } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeService, setActiveService] = useState(0);
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const formRef = useRef<HTMLFormElement>(null);

  const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://vitral.pt';

  const jsonLdWebPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Vitral | Agência de Comunicação',
    description: 'A Vitral é uma agência de comunicação e produção audiovisual em Portugal. Vídeo corporativo, branding, design e comunicação digital para marcas que querem criar relevância, confiança e impacto. Vitral. Ideias que iluminam.',
    url: siteUrl,
    inLanguage: 'pt-PT',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Vitral',
      url: siteUrl,
    },
  };

  const jsonLdOrganization = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Vitral',
    description: 'Agência de comunicação e produção audiovisual. Vídeo corporativo, branding, design e comunicação digital.',
    url: siteUrl,
    logo: `${siteUrl}/vitral-logo.png`,
    image: `${siteUrl}/og-image.jpg`,
    telephone: '+351910000000',
    email: 'geral@vitral.pt',
    priceRange: '€€',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Lavra',
      addressRegion: 'Matosinhos',
      addressCountry: 'PT',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '41.3108',
      longitude: '-8.7453',
    },
    sameAs: [
      'https://www.instagram.com/vitral.agencia/',
      'https://www.linkedin.com/company/vitralpt/',
      'https://www.youtube.com/@Vitral.agencia',
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Serviços Vitral',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Produção de Vídeo' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Conteúdo Digital' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Branding & Design' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Comunicação Estratégica' } },
      ],
    },
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      icon: 'ri-film-line',
      title: 'Produção de Vídeo',
      desc: 'Vídeos corporativos, institucionais e comerciais. Da ideia ao produto final. Contamos a história da sua marca com imagem, som e emoção.',
      image: 'https://readdy.ai/api/search-image?query=professional%20video%20production%20crew%20filming%20corporate%20scene%20with%20cinema%20camera%20on%20tripod%20modern%20studio%20setup%20with%20dramatic%20lighting%20equipment%20director%20and%20cinematographer%20at%20work%20sleek%20dark%20background%20with%20warm%20orange%20and%20amber%20accent%20lights%20high%20end%20production%20quality&width=900&height=600&seq=101&orientation=landscape'
    },
    {
      icon: 'ri-broadcast-line',
      title: 'Conteúdo Digital',
      desc: 'Criamos conteúdos estratégicos para redes sociais, plataformas digitais e campanhas online. Conteúdo que gera impacto, engagement e resultados mensuráveis.',
      image: 'https://readdy.ai/api/search-image?query=creative%20digital%20content%20creation%20team%20working%20on%20social%20media%20strategy%20with%20multiple%20screens%20showing%20analytics%20and%20video%20content%20modern%20open%20office%20with%20warm%20lighting%20creative%20agency%20atmosphere%20vibrant%20and%20energetic%20workspace&width=900&height=600&seq=102&orientation=landscape'
    },
    {
      icon: 'ri-palette-line',
      title: 'Branding & Design',
      desc: 'Identidades visuais que comunicam com clareza e distinção. Desenvolvemos marcas com propósito, coerência e personalidade para se destacarem no mercado.',
      image: 'https://readdy.ai/api/search-image?query=brand%20identity%20design%20process%20with%20logo%20sketches%20color%20palettes%20typography%20samples%20on%20clean%20white%20desk%20designer%20working%20on%20brand%20guidelines%20modern%20minimalist%20creative%20studio%20warm%20natural%20light%20professional%20branding%20materials&width=900&height=600&seq=103&orientation=landscape'
    },
    {
      icon: 'ri-megaphone-line',
      title: 'Comunicação Estratégica',
      desc: 'Estratégias de comunicação integradas que alinham mensagem, canal e público. Ajudamos marcas a comunicar com clareza, consistência e impacto real.',
      image: 'https://readdy.ai/api/search-image?query=strategic%20communication%20planning%20session%20with%20team%20around%20modern%20conference%20table%20with%20presentation%20screens%20showing%20marketing%20strategy%20charts%20and%20brand%20positioning%20maps%20creative%20agency%20meeting%20room%20warm%20professional%20atmosphere&width=900&height=600&seq=104&orientation=landscape'
    },
  ];

  const [playingVideo, setPlayingVideo] = useState<number | null>(null);

  const portfolioItems = [
    {
      id: 1,
      youtubeId: 'Sanml-Zt5_c',
      title: '16 Anos | Revista Pontos de Vista',
      category: 'Produção de Vídeo',
      categoryIcon: 'ri-film-line',
    },
    // Adicionar mais projetos aqui
  ];

  const steps = [
    {
      number: '01',
      icon: 'ri-lightbulb-line',
      title: 'Briefing & Descoberta',
      desc: 'Começamos por ouvir. Mergulhamos no universo da sua marca, percebemos os seus objetivos, o seu público e o que a torna única.',
    },
    {
      number: '02',
      icon: 'ri-compass-3-line',
      title: 'Estratégia & Conceito',
      desc: 'Com base no briefing, desenvolvemos a estratégia criativa e o conceito que vai guiar todo o projeto. Cada decisão tem um propósito.',
    },
    {
      number: '03',
      icon: 'ri-camera-line',
      title: 'Produção & Criação',
      desc: 'É aqui que a magia acontece. A nossa equipa dá vida ao conceito, seja em vídeo, design, conteúdo ou comunicação, com rigor, talento e paixão.',
    },
    {
      number: '04',
      icon: 'ri-rocket-line',
      title: 'Entrega & Impacto',
      desc: 'Entregamos o projeto final, acompanhamos os resultados e garantimos que a comunicação da sua marca gera o impacto que merece.',
    },
  ];

  const socialLinks = [
    { icon: 'ri-instagram-line', href: 'https://www.instagram.com/vitral.agencia/' },
    { icon: 'ri-linkedin-fill', href: 'https://www.linkedin.com/company/vitralpt/' },
    { icon: 'ri-youtube-line', href: 'https://www.youtube.com/@Vitral.agencia' },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('sending');
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    formData.forEach((value, key) => data.append(key, value.toString()));
    try {
      const res = await fetch('https://readdy.ai/api/form/d6s4q7sddmmni7ck975g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const primary = '#526411';
  const primaryLight = '#7a9a1e';
  const accent1 = '#b72f18';
  const accent2 = '#f98e1e';
  const base = '#d3e8b5';
  const darkBg = '#1a1f0e';
  const darkBg2 = '#141808';
  const titleFont = { fontFamily: "'Montserrat', sans-serif" };
  const bodyFont = { fontFamily: "'Montserrat', sans-serif" };

  const smoothScrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const navHeight = 72;
    const top = el.getBoundingClientRect().top + window.scrollY - navHeight;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white" style={{ ...bodyFont }}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdWebPage) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdOrganization) }}
      />

      {/* NAV */}
      <nav className={`fixed w-full z-50 transition-all duration-400 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="px-8 py-4 flex items-center gap-10">
          <a
            href="#inicio"
            onClick={e => { e.preventDefault(); smoothScrollTo('inicio'); }}
            className="flex items-center cursor-pointer"
          >
            {scrolled ? (
              <img
                src="https://public.readdy.ai/ai/img_res/4b13e887-811f-4d3c-a641-0ba06c9f85f1.png"
                alt="Vitral Logo"
                className="h-12 w-auto object-contain transition-all duration-300"
              />
            ) : (
              <img
                src="https://public.readdy.ai/ai/img_res/4b13e887-811f-4d3c-a641-0ba06c9f85f1.png"
                alt="Vitral Logo"
                className="h-12 w-auto object-contain transition-all duration-300 brightness-0 invert"
              />
            )}
          </a>
          <div className="hidden md:flex items-center gap-10 ml-auto">
            {[
              { label: 'Início', id: 'inicio' },
              { label: 'Portfólio', id: 'portfolio' },
              { label: 'Serviços', id: 'servicos' },
              { label: 'Sobre', id: 'sobre' },
              { label: 'Contacto', id: 'contacto' },
            ].map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={e => { e.preventDefault(); smoothScrollTo(item.id); }}
                className={`text-sm font-medium tracking-wide transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#526411]' : 'text-white/90 hover:text-[#d3e8b5]'}`}
              >
                {item.label}
              </a>
            ))}
          </div>
          <button onClick={() => setMenuOpen(!menuOpen)} className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer ${scrolled ? 'text-gray-900' : 'text-white'}`}>
            <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-8 py-6 flex flex-col gap-5">
            {[
              { label: 'Início', id: 'inicio' },
              { label: 'Portfólio', id: 'portfolio' },
              { label: 'Serviços', id: 'servicos' },
              { label: 'Sobre', id: 'sobre' },
              { label: 'Contacto', id: 'contacto' },
            ].map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={e => { e.preventDefault(); smoothScrollTo(item.id); setMenuOpen(false); }}
                className="text-gray-800 font-medium cursor-pointer"
              >
                {item.label}
              </a>
            ))}
          </div>
        )}
      </nav>

      {/* HERO */}
      <section id="inicio" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://storage.readdy-site.link/project_files/5c51e75d-048a-4e48-b8f2-0941ddedaf24/8f36195d-98be-4fb2-addb-77e7e04e56e1_banner.jpg?v=6bee961780bc0cca16e3666e1fe48393"
            alt="Vitral Agência"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-black/70"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
        </div>

        <div className="relative z-10 w-full max-w-6xl mx-auto px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8" style={{ backgroundColor: `${base}20`, border: `1px solid ${base}50` }}>
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: base }}></span>
            <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: base }}>Agência de Comunicação &amp; Audiovisual</span>
          </div>
          <h1 className="text-6xl md:text-8xl font-black text-white mb-6 leading-none" style={{ ...titleFont, letterSpacing: '-0.02em' }}>
            Ideias que<br />
            <span className="text-transparent bg-clip-text" style={{ backgroundImage: `linear-gradient(to right, ${base}, #eaf5d0)` }}>iluminam.</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/75 max-w-2xl mx-auto mb-12 leading-relaxed font-light">
            Comunicação criativa, produção audiovisual e branding estratégico para marcas que querem fazer a diferença.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#contacto"
              onClick={e => { e.preventDefault(); smoothScrollTo('contacto'); }}
              className="px-8 py-4 text-white rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap text-sm tracking-wide hover:opacity-90"
              style={{ backgroundColor: accent1 }}
            >
              Iniciar um Projeto
            </a>
          </div>
        </div>

        <div className="absolute bottom-10 left-0 right-0 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-white/40 text-xs tracking-widest uppercase">Scroll</span>
          <i className="ri-arrow-down-line text-white/40 text-lg"></i>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="py-28 px-8 text-white" style={{ backgroundColor: darkBg }}>
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs font-semibold tracking-widest uppercase mb-6" style={{ color: base }}>O Nosso Manifesto</p>
          <h2 className="text-4xl md:text-6xl font-black leading-tight mb-8" style={{ ...titleFont, letterSpacing: '-0.01em' }}>
            Comunicar bem é essencial para criar{' '}
            <span style={{ color: base }}>relevância</span>,{' '}
            <span style={{ color: accent2 }}>confiança</span> e{' '}
            <span style={{ color: '#f5c07a' }}>impacto</span>.
          </h2>
          <p className="text-white/60 text-lg max-w-3xl mx-auto leading-relaxed">
            Inspirados pelo conceito de vitral, onde diferentes elementos se unem para criar algo que ganha vida através da luz, o nosso trabalho procura exatamente isso: dar forma às ideias das marcas e ajudá-las a destacar-se num mundo saturado de mensagens.
          </p>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section id="portfolio" className="py-28 px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: primary }}>Portfólio</p>
            <h2 className="text-4xl md:text-6xl font-black leading-tight text-gray-900" style={{ ...titleFont, letterSpacing: '-0.01em' }}>
              O nosso{' '}
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: `linear-gradient(to right, ${primary}, ${primaryLight})` }}>trabalho.</span>
            </h2>
            <p className="text-gray-500 text-lg mt-6 max-w-xl mx-auto leading-relaxed">
              Projetos que contam histórias, criam impacto e deixam marca.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {portfolioItems.map((item) => (
              <div
                key={item.id}
                className="group relative rounded-2xl overflow-hidden border border-gray-100 transition-all duration-300 hover:border-gray-200"
                style={{ backgroundColor: `${base}20` }}
              >
                {/* Video embed area */}
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  {playingVideo === item.id ? (
                    <iframe
                      className="absolute inset-0 w-full h-full"
                      src={`https://www.youtube.com/embed/${item.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                      title={item.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <button
                      className="absolute inset-0 w-full h-full cursor-pointer group/thumb"
                      onClick={() => setPlayingVideo(item.id)}
                      aria-label={`Reproduzir ${item.title}`}
                    >
                      <img
                        src={`https://img.youtube.com/vi/${item.youtubeId}/maxresdefault.jpg`}
                        alt={item.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover/thumb:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/30 group-hover/thumb:bg-black/20 transition-all duration-300"></div>
                      <div
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <div
                          className="w-16 h-16 flex items-center justify-center rounded-full transition-all duration-300 group-hover/thumb:scale-110"
                          style={{ backgroundColor: accent1 }}
                        >
                          <i className="ri-play-fill text-white text-2xl ml-1"></i>
                        </div>
                      </div>
                    </button>
                  )}
                </div>

                {/* Info */}
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                      style={{ backgroundColor: `${base}80`, color: primary }}
                    >
                      <i className={`${item.categoryIcon} text-xs`}></i>
                      {item.category}
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 leading-snug" style={titleFont}>
                    {item.title}
                  </h3>
                </div>
              </div>
            ))}

            {/* Placeholder cards para projetos futuros */}
            {portfolioItems.length < 3 && Array.from({ length: 3 - portfolioItems.length }).map((_, i) => (
              <div
                key={`placeholder-${i}`}
                className="rounded-2xl border-2 border-dashed flex flex-col items-center justify-center text-center p-10 min-h-[260px]"
                style={{ borderColor: `${base}`, backgroundColor: `${base}15` }}
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-full mb-4" style={{ backgroundColor: `${base}60` }}>
                  <i className="ri-add-line text-2xl" style={{ color: primary }}></i>
                </div>
                <p className="text-sm font-semibold text-gray-400">Em breve</p>
                <p className="text-xs text-gray-300 mt-1">Novo projeto a caminho</p>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <a
              href="https://www.youtube.com/@Vitral.agencia"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-semibold text-sm tracking-wide transition-all hover:opacity-90 cursor-pointer whitespace-nowrap border-2"
              style={{ color: primary, borderColor: primary, backgroundColor: 'transparent' }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = primary; (e.currentTarget as HTMLAnchorElement).style.color = 'white'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent'; (e.currentTarget as HTMLAnchorElement).style.color = primary; }}
            >
              <i className="ri-youtube-line text-base"></i>
              Ver canal no YouTube
            </a>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-28 px-8 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full opacity-20 blur-3xl pointer-events-none" style={{ backgroundColor: base }}></div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-20">
            <p className="text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: primary }}>O Nosso Processo</p>
            <h2 className="text-4xl md:text-6xl font-black leading-tight text-gray-900" style={{ ...titleFont, letterSpacing: '-0.01em' }}>
              É assim que a{' '}
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: `linear-gradient(to right, ${primary}, ${primaryLight})` }}>magia acontece.</span>
            </h2>
            <p className="text-gray-500 text-lg mt-6 max-w-xl mx-auto leading-relaxed">
              Um processo claro, criativo e orientado para resultados, do primeiro contacto ao produto final.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-0 relative">


            {steps.map((step, i) => (
              <div key={i} className="relative flex flex-col items-center text-center px-6 group">
                <div className="relative mb-6">
                  <div
                    className="w-28 h-28 rounded-full flex items-center justify-center border transition-all duration-300 group-hover:scale-105"
                    style={{ backgroundColor: `${base}50`, borderColor: `${base}` }}
                  >
                    <i className={`${step.icon} text-4xl`} style={{ color: primary }}></i>
                  </div>
                  <span
                    className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-xs font-black"
                    style={{ backgroundColor: accent1, color: 'white' }}
                  >
                    {step.number}
                  </span>
                </div>
                <h3 className="text-lg font-bold mb-3 leading-snug text-gray-900" style={titleFont}>
                  {step.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                {i < steps.length - 1 && (
                  <div className="md:hidden flex justify-center my-6">
                    <i className="ri-arrow-down-line text-2xl" style={{ color: 'rgba(0,0,0,0.15)' }}></i>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-20 text-center">
            <a
              href="#contacto"
              onClick={e => { e.preventDefault(); smoothScrollTo('contacto'); }}
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-semibold text-white text-sm tracking-wide transition-all hover:opacity-90 cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: accent1 }}
            >
              <i className="ri-sparkling-line text-base"></i>
              Vamos começar o seu projeto
            </a>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="servicos" className="py-28 px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <p className="text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: primary }}>O Que Fazemos</p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight" style={{ ...titleFont, letterSpacing: '-0.01em' }}>
              Serviços que transformam<br />ideias em resultados.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-12">
            {services.map((s, i) => (
              <button
                key={i}
                onClick={() => setActiveService(i)}
                className="text-left p-7 rounded-2xl transition-all cursor-pointer border"
                style={
                  activeService === i
                    ? { backgroundColor: darkBg, color: 'white', borderColor: darkBg }
                    : { backgroundColor: `${base}40`, color: '#1f2937', borderColor: 'transparent' }
                }
              >
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl mb-5"
                  style={activeService === i ? { backgroundColor: accent1 } : { backgroundColor: base }}
                >
                  <i className={`${s.icon} text-2xl`} style={{ color: activeService === i ? 'white' : primary }}></i>
                </div>
                <h3 className="text-xl font-bold mb-3" style={titleFont}>{s.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: activeService === i ? 'rgba(255,255,255,0.7)' : '#4b5563' }}>{s.desc}</p>
              </button>
            ))}
          </div>


        </div>
      </section>

      {/* ABOUT */}
      <section id="sobre" className="py-28 px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-20 items-center">
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase mb-6" style={{ color: primary }}>Sobre a Vitral</p>
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-8 leading-tight" style={{ ...titleFont, letterSpacing: '-0.01em' }}>
                Uma agência criada para fazer marcas brilhar.
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-6">
                A <strong>Vitral</strong> é uma agência de comunicação e produção audiovisual criada para ajudar empresas e marcas a comunicar de forma mais clara, criativa e estratégica. Acreditamos que comunicar bem é essencial para criar relevância, confiança e impacto.
              </p>
              <p className="text-gray-600 text-base leading-relaxed mb-6">
                Aliamos <strong>criatividade</strong>, <strong>estratégia</strong> e <strong>produção</strong> para transformar ideias em comunicação visual moderna, eficaz e orientada para resultados.
              </p>
              <p className="text-base leading-relaxed italic border-l-4 pl-5" style={{ color: '#7a7a7a', borderColor: accent2 }}>
                "Vitral. Ideias que iluminam."
              </p>
            </div>
            <div className="relative">
              <div className="w-full h-[520px] rounded-2xl overflow-hidden">
                <img
                  src="https://storage.readdy-site.link/project_files/5c51e75d-048a-4e48-b8f2-0941ddedaf24/da288c49-0363-497e-9cc5-a84399d0b540_logo_versions_Logo-cpia-2.png?v=116faab875da4da228672047fa2adb1b"
                  alt="Equipa Vitral"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="py-24 px-8 text-white text-center relative overflow-hidden" style={{ backgroundColor: darkBg }}>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `radial-gradient(circle at 30% 50%, ${accent2}40, transparent 60%), radial-gradient(circle at 70% 50%, ${accent1}30, transparent 60%)` }}></div>
        <div className="max-w-3xl mx-auto relative z-10">
          <h2 className="text-4xl md:text-6xl font-black mb-6 leading-tight" style={{ ...titleFont, letterSpacing: '-0.01em' }}>
            Pronto para dar vida à sua marca?
          </h2>
          <p className="text-white/60 text-lg mb-10 leading-relaxed">
            Vamos conversar sobre o seu projeto. Sem compromisso e com toda a nossa atenção.
          </p>
          <a
            href="#contacto"
            onClick={e => { e.preventDefault(); smoothScrollTo('contacto'); }}
            className="inline-block px-10 py-4 text-white rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap text-sm tracking-wide hover:opacity-90"
            style={{ backgroundColor: accent1 }}
          >
            Iniciar Conversa
          </a>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contacto" className="py-28 px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-20">
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase mb-6" style={{ color: primary }}>Contacto</p>
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-8 leading-tight" style={{ ...titleFont, letterSpacing: '-0.01em' }}>
                Vamos criar algo extraordinário juntos.
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-10">
                Tem um projeto em mente? Quer saber mais sobre os nossos serviços? Fale connosco.
              </p>
              <div className="space-y-6">
                {[
                  { icon: 'ri-mail-send-line', label: 'Email', value: 'geral@vitral.pt' },
                  { icon: 'ri-map-pin-2-line', label: 'Localização', value: 'Matosinhos' },
                ].map((c, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: base }}>
                      <i className={`${c.icon} text-xl`} style={{ color: primary }}></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">{c.label}</p>
                      <p className="text-gray-800 font-semibold">{c.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3 mt-10">
                {socialLinks.map((s, i) => (
                  <a key={i} href={s.href} target="_blank" rel="nofollow noopener noreferrer"
                    className="w-11 h-11 flex items-center justify-center rounded-xl transition-all cursor-pointer text-gray-600 hover:text-white"
                    style={{ backgroundColor: `${base}60` }}
                    onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = accent1; (e.currentTarget as HTMLAnchorElement).style.color = 'white'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = `${base}60`; (e.currentTarget as HTMLAnchorElement).style.color = ''; }}
                  >
                    <i className={`${s.icon} text-lg`}></i>
                  </a>
                ))}
              </div>
            </div>

            <div className="p-8 rounded-2xl" style={{ backgroundColor: `${base}30` }}>
              {formStatus === 'success' ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-16">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full mb-5" style={{ backgroundColor: base }}>
                    <i className="ri-check-line text-3xl" style={{ color: primary }}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2" style={titleFont}>Mensagem enviada!</h3>
                  <p className="text-gray-500 text-sm">Entraremos em contacto em breve. Obrigado!</p>
                  <button onClick={() => setFormStatus('idle')} className="mt-6 text-sm font-semibold cursor-pointer hover:underline" style={{ color: primary }}>Enviar outra mensagem</button>
                </div>
              ) : (
                <form ref={formRef} data-readdy-form id="vitral-contact-form" onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Nome</label>
                      <input type="text" name="nome" required placeholder="O seu nome" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none bg-white" onFocus={e => (e.currentTarget.style.borderColor = primary)} onBlur={e => (e.currentTarget.style.borderColor = '')} />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Empresa</label>
                      <input type="text" name="empresa" placeholder="Nome da empresa" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none bg-white" onFocus={e => (e.currentTarget.style.borderColor = primary)} onBlur={e => (e.currentTarget.style.borderColor = '')} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Email</label>
                    <input type="email" name="email" required placeholder="seu@email.com" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none bg-white" onFocus={e => (e.currentTarget.style.borderColor = primary)} onBlur={e => (e.currentTarget.style.borderColor = '')} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Serviço de Interesse</label>
                    <select name="servico" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none bg-white cursor-pointer" onFocus={e => (e.currentTarget.style.borderColor = primary)} onBlur={e => (e.currentTarget.style.borderColor = '')}>
                      <option value="producao_video">Produção de Vídeo</option>
                      <option value="conteudo_digital">Conteúdo Digital</option>
                      <option value="branding">Branding &amp; Design</option>
                      <option value="comunicacao">Comunicação Estratégica</option>
                      <option value="outro">Outro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Mensagem</label>
                    <textarea name="mensagem" rows={4} required maxLength={500} placeholder="Conte-nos sobre o seu projeto..." className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none bg-white resize-none" onFocus={e => (e.currentTarget.style.borderColor = primary)} onBlur={e => (e.currentTarget.style.borderColor = '')}></textarea>
                    <p className="text-xs text-gray-400 mt-1">Máximo 500 caracteres</p>
                  </div>
                  {formStatus === 'error' && (
                    <p className="text-red-500 text-sm">Erro ao enviar. Por favor, tente novamente.</p>
                  )}
                  <button type="submit" disabled={formStatus === 'sending'} className="w-full py-4 text-white rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap text-sm tracking-wide disabled:opacity-60 hover:opacity-90" style={{ backgroundColor: darkBg }}>
                    {formStatus === 'sending' ? 'A enviar...' : 'Enviar Mensagem'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="text-white py-16 px-8" style={{ backgroundColor: darkBg2 }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center mb-4">
                <img
                  src="https://public.readdy.ai/ai/img_res/4b13e887-811f-4d3c-a641-0ba06c9f85f1.png"
                  alt="Vitral Logo"
                  className="h-12 w-auto object-contain brightness-0 invert"
                />
              </div>
              <p className="text-white/50 text-sm leading-relaxed max-w-xs">
                Agência de comunicação e produção audiovisual. Criatividade, estratégia e produção ao serviço das marcas.
              </p>
              <p className="text-sm font-medium mt-4 italic" style={{ color: base }}>Ideias que iluminam.</p>
            </div>
            <div>
              <h4 className="font-bold text-sm mb-5 uppercase tracking-widest text-white/40">Serviços</h4>
              <ul className="space-y-3">
                {['Produção de Vídeo', 'Conteúdo Digital', 'Branding & Design', 'Comunicação Estratégica'].map((s) => (
                  <li key={s}>
                    <a
                      href="#servicos"
                      onClick={e => { e.preventDefault(); smoothScrollTo('servicos'); }}
                      className="text-white/60 text-sm transition-colors cursor-pointer hover:text-white"
                    >
                      {s}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-sm mb-5 uppercase tracking-widest text-white/40">Empresa</h4>
              <ul className="space-y-3">
                {[{ label: 'Sobre Nós', id: 'sobre' }, { label: 'Contacto', id: 'contacto' }].map(({ label, id }) => (
                  <li key={label}>
                    <a
                      href={`#${id}`}
                      onClick={e => { e.preventDefault(); smoothScrollTo(id); }}
                      className="text-white/60 text-sm transition-colors cursor-pointer hover:text-white"
                    >
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/30 text-xs">&copy; 2026 Vitral. Todos os direitos reservados.</p>
            <div className="flex gap-3">
              {socialLinks.map(({ icon, href }) => (
                <a key={icon} href={href} target="_blank" rel="nofollow noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center rounded-lg transition-all cursor-pointer text-white/50 hover:text-white"
                  style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = accent1)}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)')}
                >
                  <i className={`${icon} text-base`}></i>
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
